import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Car, Shield, Clock, Award, ChevronLeft, ChevronRight, ShieldCheck } from 'lucide-react';
import { getAvailableCars } from '../services/firebaseService';
import { carSlug } from '../utils/carSlug';
import { setPageMeta } from '../utils/seo';

const HomePage = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    setPageMeta({
      title: 'Viaturas Usadas em Ovar',
      description: 'Diamond Cars em Ovar — compre a sua viatura usada com garantia e confiança. Stock atualizado, preços competitivos e atendimento personalizado.',
      path: '/'
    });

    const fetchCars = async () => {
      const carsData = await getAvailableCars();
      setCars(carsData);
      setLoading(false);
    };
    fetchCars();
  }, []);

  // Carousel controls
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.max(1, cars.length));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + cars.length) % Math.max(1, cars.length));
  };

  // Auto-advance carousel
  useEffect(() => {
    if (cars.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % cars.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [cars.length]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price || 0);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section - Foto do Stand */}
      <section className="relative h-[600px] bg-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/ALX08396.jpeg"
            alt="Diamond Cars Stand"
            className="w-full h-full object-cover brightness-50"
          />
        </div>

        <div className="relative h-full container mx-auto px-4 flex items-center">
          <div className="max-w-3xl text-white">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              {process.env.REACT_APP_COMPANY_NAME || 'Diamond Cars'}
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8">
              O seu parceiro de confiança para viaturas de qualidade
            </p>
            <p className="text-lg text-blue-200 mb-10 max-w-2xl">
              Oferecemos uma seleção premium de viaturas usadas certificadas, com garantia e os melhores preços do mercado.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/viaturas"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-md font-semibold text-lg transition shadow-lg hover:shadow-xl"
              >
                Ver Viaturas
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-blue-900 px-8 py-4 rounded-md font-semibold text-lg transition shadow-lg"
              >
                Contacte-nos
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Carousel de Carros em Destaque */}
      {!loading && cars.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Viaturas em Destaque
              </h2>
              <p className="text-xl text-gray-600">
                Explore algumas das nossas melhores ofertas
              </p>
            </div>

            <div className="relative max-w-5xl mx-auto">
              {/* Carousel */}
              <div className="relative h-[500px] rounded-xl overflow-hidden shadow-2xl">
                {cars.map((car, index) => (
                  <div
                    key={car._id}
                    className={`absolute inset-0 transition-opacity duration-700 ${
                      index === currentSlide ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                    }`}
                  >
                    <Link to={`/carro/${carSlug(car)}`} className="block h-full">
                      <div className="relative h-full">
                        {car.images && car.images.length > 0 ? (
                          <img
                            src={car.images[0]}
                            alt={`${car.marca} ${car.modelo}`}
                            className={`w-full h-full object-cover ${car.vendido ? 'opacity-80' : ''}`}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                            <Car className="w-32 h-32 text-gray-400" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                        {/* Watermark VENDIDO */}
                        {car.vendido && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-red-600 text-white px-8 py-4 rounded-lg shadow-xl transform -rotate-12">
                              <span className="text-4xl font-bold tracking-wider">VENDIDO</span>
                            </div>
                          </div>
                        )}

                        {/* Car Info */}
                        <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                          <h3 className="text-3xl md:text-4xl font-bold mb-2">
                            {car.marca} {car.modelo}
                          </h3>
                          <p className={`text-2xl md:text-3xl font-bold ${car.vendido ? 'text-gray-400' : 'text-white'} mb-4`}>
                            {formatPrice(car.preco)}
                          </p>
                          <div className="flex gap-4 text-sm text-blue-400 font-bold">
                            <span>{car.ano}</span>
                            <span>•</span>
                            <span>{car.km?.toLocaleString('pt-PT')} km</span>
                            {car.combustivel && (
                              <>
                                <span>•</span>
                                <span>{car.combustivel}</span>
                              </>
                            )}
                            {car.garantiaMeses > 0 && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1 font-bold">
                                  <ShieldCheck className="w-3 h-3" />
                                  {car.garantiaMeses} Meses
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}

                {/* Navigation */}
                {cars.length > 1 && (
                  <>
                    <button
                      onClick={prevSlide}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm p-3 rounded-full transition z-10"
                    >
                      <ChevronLeft className="w-6 h-6 text-white" />
                    </button>
                    <button
                      onClick={nextSlide}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm p-3 rounded-full transition z-10"
                    >
                      <ChevronRight className="w-6 h-6 text-white" />
                    </button>
                  </>
                )}

                {/* Dots */}
                {cars.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {cars.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`h-2 rounded-full transition ${
                          index === currentSlide ? 'bg-white w-8' : 'bg-white/50 w-2'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Ver Todas */}
              <div className="text-center mt-8">
                <Link
                  to="/viaturas"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-lg"
                >
                  Ver Todas as Viaturas
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Destaques / Benefícios */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Porquê Escolher-nos?
            </h2>
            <p className="text-xl text-gray-600">
              Garantimos qualidade e confiança em cada viatura
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Garantia Incluída
              </h3>
              <p className="text-gray-600">
                Todas as viaturas com garantia e suporte pós-venda
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Qualidade Certificada
              </h3>
              <p className="text-gray-600">
                Viaturas inspecionadas e certificadas
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Atendimento Rápido
              </h3>
              <p className="text-gray-600">
                Resposta em 24h e processo ágil
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Car className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Grande Seleção
              </h3>
              <p className="text-gray-600">
                Viaturas de todas as marcas e segmentos
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Pronto para Encontrar o Seu Carro?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Explore a nossa seleção de viaturas de qualidade ou entre em contacto para mais informações
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/viaturas"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-blue-900 px-8 py-4 rounded-md font-semibold text-lg transition shadow-lg"
            >
              Ver Todas as Viaturas
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-md font-semibold text-lg transition border-2 border-blue-500"
            >
              Fale Connosco
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
